import * as React from 'react';

import { useRequest } from 'ahooks';
import {
  Alert,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Spin,
  TreeSelect,
  Typography,
} from 'antd';

import {
  DecimalInputNumber,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
  PrimitiveTreeSelect,
} from 'components';
import { useShippingConfigAction, useShippingConfigValue } from 'context';
import { getBrands, getCategories, getCities, searchProducts } from 'services';
import { CityTree, ShippingAmountType } from 'types';
import {
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  localizeShippingAmountType,
  mapProvinceToTree,
  normalizeToString,
  PATHNAME,
} from 'utils';

const {
  QUERY_PARAM: {
    DEFAULT: {
      SORT_TYPE: DEFAULT_SORT_TYPE,
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;

const shippingAmountTypes = [
  'Percentage',
  'Price',
] satisfies ShippingAmountType[];

type ProductsParams = Parameters<typeof searchProducts>[number];

type FieldType = Pick<
  ReturnType<typeof useShippingConfigValue>,
  | 'title'
  | 'amountType'
  | 'amount'
  | 'products'
  | 'brands'
  | 'categories'
  | 'cities'
>;

const ShippingConfigFormContent = () => {
  const {
    title,
    amountType,
    products: initialProducts = [],
    brands: initialBrands = [],
    categories: initialCategories = [],
    cities: initialCities = [],
  } = useShippingConfigValue();
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });
  const { data: citiesData, loading: citiesLoading } = useRequest(getCities, {
    cacheKey: 'cities',
  });
  const {
    data: productsData,
    error: productsError,
    params: productsParams,
    loading: isProductsLoading,
    run: searchProductsRun,
  } = useRequest(
    async ({ searchKey }: Pick<ProductsParams, 'searchKey'>) =>
      await searchProducts({
        searchKey,
        sortType: DEFAULT_SORT_TYPE,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_MAX_PAGE_SIZE,
      }),
    {
      defaultParams: [{ searchKey: '' }],
      debounceWait: 1000,
    },
  );
  const action = useShippingConfigAction();

  const { label: amountTypeLabel } = localizeShippingAmountType(amountType);
  const { categories = [] } = categoriesData ?? {};
  const { brands = [] } = brandsData ?? {};
  const { provinces = [] } = citiesData ?? {};
  const { total: totalProducts = 0, data: products = [] } = productsData ?? {};
  const { searchKey: currentSearchKey = '' } = productsParams.at(0) ?? {};

  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );
  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const productsSelectOptions = generateSelectOptions(
    products,
    'id',
    'name',
    'id',
  );

  const citiesTreeData = generateTreeSelectData(
    provinces.map(p => mapProvinceToTree(p)),
    'id',
    'name',
    'id',
  );

  return (
    <Row className='px-6 py-8'>
      <Col span={24} lg={24} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='عنوان'
          name='title'
          rules={[inputRule.required, inputRule.string]}
          initialValue={title}
        >
          <Input placeholder='عنوان تنظیمات ارسال را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='نوع هزینه'
          name='amountType'
          rules={[inputRule.required]}
          initialValue={amountType}
        >
          <Select<ShippingAmountType | undefined>
            allowClear
            placeholder='نوع هزینه ارسال را انتخاب کنید'
            options={shippingAmountTypes.map(type => {
              const { label } = localizeShippingAmountType(type);
              return {
                label,
                value: type,
              };
            })}
            onChange={value => {
              action({
                type: 'UPDATE_SHIPPING_CONFIG_AMOUNT_TYPE',
                payload: {
                  amountType: value,
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType>
          label='مقدار'
          name='amount'
          rules={[inputRule.required, inputRule.digit]}
        >
          <DecimalInputNumber
            disabled={!amountType}
            addonAfter={amountTypeLabel}
            max={amountType === 'Percentage' ? 100 : undefined}
            placeholder='مقدار هزینه ارسال را وارد کنید'
            onChange={value => {
              if (value === null) return;

              action({
                type: 'UPDATE_SHIPPING_CONFIG_AMOUNT',
                payload: {
                  amount: Number(value),
                },
              });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Divider dashed className='mb-4 mt-0' />
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='برندها'
          name='brands'
          initialValue={initialBrands.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          }))}
        >
          <Select
            allowClear
            labelInValue
            mode='multiple'
            placeholder='برندهای تنظیمات ارسال را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='دسته‌بندی‌ها'
          name='categories'
          initialValue={initialCategories.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          }))}
        >
          <PrimitiveTreeSelect
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های تنظیمات ارسال را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='محصول‌ها'
          name='products'
          initialValue={initialProducts.map(({ id, name }) => ({
            key: id,
            label: name,
            value: id,
          }))}
        >
          <Select
            allowClear
            labelInValue
            filterOption={false}
            popupMatchSelectWidth={false}
            mode='multiple'
            placeholder='محصول‌های تنظیمات ارسال را انتخاب کنید'
            options={productsSelectOptions}
            notFoundContent={
              isProductsLoading ? (
                <div className='grid min-h-24 place-content-center gap-4'>
                  <Spin />
                  <Typography.Text className='text-[--ant-color-info]'>
                    در حال بارگذاری محصول‌ها...
                  </Typography.Text>
                </div>
              ) : productsError ? (
                <div className='grid min-h-24 content-center justify-items-center gap-4'>
                  <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                  <Typography.Text type='danger'>
                    {productsError instanceof Error
                      ? productsError.message
                      : String(productsError)}
                  </Typography.Text>
                </div>
              ) : !totalProducts ? (
                <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                  <InboxOutlinedIcon className='text-3xl' />
                  <Typography.Text type='secondary'>
                    <span>هیچ محصولی با عنوان</span>
                    <span> &rdquo;{currentSearchKey}&rdquo; </span>
                    <span>وجود ندارد.</span>
                  </Typography.Text>
                </div>
              ) : undefined
            }
            onSearch={value => {
              const searchKey = value.trim();
              searchProductsRun({ searchKey });
            }}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='شهرها'
          name='cities'
          initialValue={initialCities.map(({ id, name }) => ({
            key: id,
            label: name,
            value: id,
          }))}
        >
          <PrimitiveTreeSelect
            multiple
            treeCheckable
            labelInValue
            placeholder='شهرهای تنظیمات ارسال را انتخاب کنید'
            loading={citiesLoading}
            treeData={citiesTreeData}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Flex vertical gap='small'>
          <Alert
            showIcon
            type='warning'
            message='هزینه ارسال نهایی، بر اشتراک کلیه گزینه‌های انتخاب‌شده (برندها، دسته‌بندی‌ها، محصول‌ها و شهرها)، محاسبه و اعمال می‌گردد.'
          />
        </Flex>
      </Col>
    </Row>
  );
};

export { ShippingConfigFormContent };
